import DynamicFormRenderer from '@aims/shared/report-schedules/DynamicFormRenderer';
import {
  ScheduleFrequencies,
  ScheduleEntryStatuses,
} from '@aims/shared/report-schedules/constants';
import {
  extractScheduledReportForAdmin,
  reviewScheduledReportForAdminMutation,
  scheduledReportForAdminQuery,
  scheduledReportUpdatesForAdminSubscription,
} from '@aims/shared/report-schedules/scheduled-report-constants';
import {
  updateReportAction,
  createReportAction,
  deleteReportAction,
} from '@aims/shared/redux-store/reports-store';
import useOneM1 from '@aims/shared/shared/use-one-m1';
import useSingleSomethingA10 from '@aims/shared/shared/use-single-something-a10';
import useSingleSomethingUpdatesA10 from '@aims/shared/shared/use-single-something-updates-a10';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Skeleton,
  Space,
  Tag,
  Typography,
  notification,
} from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import CSPage from '../../components/CSPage';
import CSPageHeader from '../../components/CSPageHeader';
import { allReportScheduleTemplatesQuery } from '../report-schedule-templates/constants';

const queryId = 'reviewScheduledReport';
const { Title, Paragraph, Text } = Typography;

function ReviewScheduledReportPage() {
  const { reportId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  useSingleSomethingUpdatesA10({
    variables: { _id: reportId },
    subscription: scheduledReportUpdatesForAdminSubscription,
    createAction: createReportAction,
    updateAction: updateReportAction,
    deleteAction: deleteReportAction,
    extractSomething: (resp) => resp?.data?.scheduledReportUpdatesForAdmin,
    queryId,
    skip: !reportId,
  });

  const { loading: reportLoading } = useOneM1({
    variables: { _id: reportId },
    query: scheduledReportForAdminQuery,
    extract: extractScheduledReportForAdmin,
    queryId,
    fetchPolicy: 'network-only',
    updateAction: updateReportAction,
    skip: !reportId,
    showError: true,
  });
  const report = useSingleSomethingA10('reports', reportId, queryId);

  // Load the template to display form fields
  const { data: templatesData } = useQuery(allReportScheduleTemplatesQuery, {
    variables: { first: 200, filters: {} },
    fetchPolicy: 'cache-and-network',
  });
  const template = useMemo(() => {
    if (!report?.templateId || !templatesData) return null;
    const edges = templatesData?.allReportScheduleTemplates?.edges || [];
    return edges.map((e) => e.node).find((t) => t._id === report.templateId);
  }, [report, templatesData]);

  const [reviewReport] = useMutation(reviewScheduledReportForAdminMutation);

  const handleApprove = useCallback(async () => {
    setLoading(true);
    try {
      await reviewReport({
        variables: { reportId, status: 'APPROVED' },
      });
      notification.success({
        message: 'Approved',
        description: 'Report has been approved',
      });
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: 'Failed to approve report',
      });
    }
    setLoading(false);
  }, [reportId, reviewReport]);

  const handleReject = useCallback(async () => {
    setLoading(true);
    try {
      await reviewReport({
        variables: { reportId, status: 'REJECTED' },
      });
      notification.success({
        message: 'Rejected',
        description: 'Report has been rejected',
      });
    } catch (err) {
      console.error(err);
      notification.error({
        message: 'Error',
        description: 'Failed to reject report',
      });
    }
    setLoading(false);
  }, [reportId, reviewReport]);

  return (
    <>
      <CSPage
        containerStyle={{ maxWidth: 800 }}
        title="Review Scheduled Report"
      >
        <CSPageHeader
          title="Review Scheduled Report"
          backActions={[
            <Button
              key="back"
              type="text"
              onClick={() => history.goBack()}
              icon={<ArrowLeftOutlined />}
            >
              Go back
            </Button>,
          ]}
        />
        {reportLoading && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Skeleton active />
            <Skeleton active />
          </Space>
        )}
        {report && (
          <>
            <Card style={{ marginBottom: 16 }}>
              <Descriptions title="Report Details" bordered column={2}>
                <Descriptions.Item label="ID">
                  {report.humanReadableId || report._id}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag
                    color={
                      report.status === 'APPROVED'
                        ? 'green'
                        : report.status === 'PENDING' || report.isSubmitted
                        ? 'gold'
                        : report.status === 'REJECTED'
                        ? 'red'
                        : 'default'
                    }
                  >
                    {report.status || 'Draft'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Contact">
                  {report.contact?.fullName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Project #">
                  {report.holisticProject?.projectNumber ||
                    report.holisticProject?.projectNo ||
                    '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Project">
                  {report.holisticProject?.projectName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Coordinator">
                  {report.holisticProject?.regionalCoordinator?.fullName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Region">
                  {report.holisticProject?.region ||
                    report.holisticProject?.regionCode ||
                    report.regionName ||
                    report.regionCode ||
                    '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Frequency">
                  <Tag
                    color={
                      ScheduleFrequencies[report.frequency]?.color || 'default'
                    }
                  >
                    {ScheduleFrequencies[report.frequency]?.label ||
                      report.frequency}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Due Date">
                  {report.dueDate
                    ? dayjs(report.dueDate).format('MMMM D, YYYY')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Submitted">
                  {report.submittedAt
                    ? dayjs(report.submittedAt).format('MMMM D, YYYY h:mm A')
                    : 'Not yet submitted'}
                </Descriptions.Item>
                <Descriptions.Item label="Template">
                  {template?.name || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {template && report.formData && (
              <Card title="Report Form Data" style={{ marginBottom: 16 }}>
                <DynamicFormRenderer
                  formFields={template.formFields || []}
                  formData={
                    typeof report.formData === 'string'
                      ? JSON.parse(report.formData)
                      : report.formData || {}
                  }
                  readOnly
                />
              </Card>
            )}

            {report.notes && (
              <Card title="Notes" style={{ marginBottom: 16 }}>
                <Paragraph>{report.notes}</Paragraph>
              </Card>
            )}

            {report.adminNotes && (
              <Card title="Admin Notes" style={{ marginBottom: 16 }}>
                <Paragraph>{report.adminNotes}</Paragraph>
              </Card>
            )}

            {report.isSubmitted &&
              report.status !== 'APPROVED' &&
              report.status !== 'REJECTED' && (
                <Card>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 16,
                    }}
                  >
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={handleApprove}
                      loading={loading}
                      size="large"
                    >
                      Approve Report
                    </Button>
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={handleReject}
                      loading={loading}
                      size="large"
                    >
                      Reject Report
                    </Button>
                  </div>
                </Card>
              )}
          </>
        )}
      </CSPage>
      <style jsx>{``}</style>
    </>
  );
}

export default ReviewScheduledReportPage;
